import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    Select,
    Tag,
    Switch,
    App,
    Popconfirm,
    Alert,
    Descriptions,
    Typography,
} from 'antd'
import {
    UserAddOutlined,
    EditOutlined,
    SearchOutlined,
    DeleteOutlined,
    KeyOutlined,
    StopOutlined,
    CopyOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../../components/common/FilterBar'
import {
    PaginatedListCard,
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../../components/common/PaginatedListTable'
import { PageLayout } from '../../components/layout'
import { useAuth } from '../../hooks/useAuth'
import { usePermissions } from '../../hooks/usePermissions'
import {
    getUsers,
    createUser,
    updateUser,
    updateCurrentUserPassword,
    updateManagedUserPassword,
    deleteUser,
    resetUserApiCredentials,
    revokeUserApiCredentials,
    AdminUser,
    UpdateUserRequest,
    ResetUserApiCredentialsResponse,
} from '../../services/adminService'
import {
    getActiveRoles,
    GetActiveRolesParams,
    Role,
} from '../../services/rbacService'
import { localeService } from '../../services/localeService'
import { RegionSelect } from '../../components/common/RegionSelect'
import { copyTextToClipboard } from '../../utils/copyToClipboard'
import { getLocalizedApiErrorMessage } from '../../utils/apiErrorI18n'
import {
    formatRoleOptionLabel,
    getRoleBuiltinDescription,
    getRoleDisplayName,
} from '../../utils/roleDisplay'
import { isPasswordPolicyValid } from '../../utils/passwordPolicy'
import { normalizeSearchText } from '../../utils/searchText'

const PROJECT_MANAGER_ROLE_NAME = 'project_manager'
const USER_MODAL_WIDTH = 560
const USERNAME_MIN_LENGTH = 4
const USERNAME_MAX_LENGTH = 32
const USERNAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_]*$/
const DISPLAY_NAME_PATTERN = /^[\u4e00-\u9fffA-Za-z0-9_]+$/

const isProjectUser = (user: AdminUser): boolean =>
    user.account_type === 'project' || user.home_project_id != null

const isFormValidationError = (
    error: unknown
): error is { errorFields: unknown[] } =>
    typeof error === 'object' && error !== null && 'errorFields' in error

const tableActionStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, max-content)',
    justifyContent: 'start',
    columnGap: 28,
    rowGap: 12,
    alignItems: 'center',
}

const modalBodyStyle: React.CSSProperties = {
    paddingTop: 8,
}

const autofillDecoyStyle: React.CSSProperties = {
    position: 'absolute',
    left: -9999,
    top: -9999,
    width: 1,
    height: 1,
    opacity: 0,
    pointerEvents: 'none',
}

const UserManagement: React.FC = () => {
    const { message, modal } = App.useApp()
    const { t } = useTranslation('admin')
    const formatApiError = useCallback(
        (error: unknown, fallback: string) =>
            getLocalizedApiErrorMessage(error, fallback, t),
        [t]
    )
    const { user: currentLoginUser, logout } = useAuth()
    const {
        canAssignRoles,
        canCreateUsers,
        canDeleteUsers,
        canManageUserApiCredentials,
        canReadRoles,
        canUpdateUserPasswords,
        canUpdateUsers,
    } = usePermissions()

    const [users, setUsers] = useState<AdminUser[]>([])
    const [roles, setRoles] = useState<Role[]>([])
    const [loading, setLoading] = useState(false)
    const [rolesLoading, setRolesLoading] = useState(false)
    const [rolesLoadError, setRolesLoadError] = useState(false)
    const [userSubmitting, setUserSubmitting] = useState(false)
    const userSubmittingRef = useRef(false)
    const [userModalVisible, setUserModalVisible] = useState(false)
    const [apiCredentialModalVisible, setApiCredentialModalVisible] =
        useState(false)
    const [currentUser, setCurrentUser] = useState<AdminUser | null>(null)
    const [apiCredentialUser, setApiCredentialUser] =
        useState<AdminUser | null>(null)
    const [generatedApiCredentials, setGeneratedApiCredentials] =
        useState<ResetUserApiCredentialsResponse | null>(null)
    const [apiCredentialLoading, setApiCredentialLoading] = useState(false)
    const [
        shouldReloadUsersAfterCredentialChange,
        setShouldReloadUsersAfterCredentialChange,
    ] = useState(false)
    const [form] = Form.useForm()
    const [userFormInstanceKey, setUserFormInstanceKey] = useState(0)
    const userFormOpenSeqRef = useRef(0)
    const [passwordForm] = Form.useForm()
    const [passwordModalVisible, setPasswordModalVisible] = useState(false)
    const [passwordUser, setPasswordUser] = useState<AdminUser | null>(null)
    const [passwordLoading, setPasswordLoading] = useState(false)
    const [total, setTotal] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [usernameKeyword, setUsernameKeyword] = useState('')
    const [appliedUsernameKeyword, setAppliedUsernameKeyword] = useState('')
    const [openDeleteUserId, setOpenDeleteUserId] = useState<number | null>(
        null
    )
    const canEditUserRole = canAssignRoles || canUpdateUsers
    const managedUserAccountInputId = useMemo(
        () => `managed-user-account-${userFormInstanceKey}`,
        [userFormInstanceKey]
    )
    const managedUserAccountInputName = useMemo(
        () => `managed_account_value_${userFormInstanceKey}`,
        [userFormInstanceKey]
    )

    const loadUsers = useCallback(
        async (page = 1, size = 10, username = '') => {
            setLoading(true)
            try {
                const normalizedUsername = normalizeSearchText(username)
                const data = await getUsers({
                    skip: (page - 1) * size,
                    limit: size,
                    username: normalizedUsername || undefined,
                })
                setUsers(data.users || [])
                setTotal(data.total || 0)
            } catch (error: unknown) {
                message.error(
                    formatApiError(error, t('user.messages.loadFailed'))
                )
            } finally {
                setLoading(false)
            }
        },
        [formatApiError, message, t]
    )

    const loadRoles = useCallback(async (params?: GetActiveRolesParams) => {
        setRolesLoading(true)
        setRolesLoadError(false)
        try {
            const data = await getActiveRoles(params)
            if (params?.targetAccountType === 'platform') {
                setRoles(data.filter((role) => role.scope_type === 'platform'))
            } else {
                setRoles(data)
            }
        } catch (error: unknown) {
            setRoles([])
            setRolesLoadError(true)
            console.error('Failed to load roles:', error)
            message.error(
                formatApiError(error, t('user.messages.roleLoadFailed'))
            )
        } finally {
            setRolesLoading(false)
        }
    }, [formatApiError, message, t])

    const syncUserInState = useCallback(
        (userId: number, updater: (user: AdminUser) => AdminUser) => {
            setUsers((prevUsers) =>
                prevUsers.map((user) =>
                    user.id === userId ? updater(user) : user
                )
            )
            setApiCredentialUser((prevUser) => {
                if (!prevUser || prevUser.id !== userId) {
                    return prevUser
                }
                return updater(prevUser)
            })
        },
        []
    )

    const getApiCredentialStatusTag = useCallback(
        (hasApiCredentials?: boolean) => (
            <Tag color={hasApiCredentials ? 'green' : 'default'}>
                {hasApiCredentials
                    ? t('user.apiCredentials.statusConfigured')
                    : t('user.apiCredentials.statusNotConfigured')}
            </Tag>
        ),
        [t]
    )

    const roleSelectOptions = useMemo(() => {
        if (!currentUser?.role) {
            return roles
        }

        if (roles.some((role) => role.name === currentUser.role)) {
            return roles
        }

        return [
            ...roles,
            {
                id: -1,
                name: currentUser.role,
                description: getRoleBuiltinDescription(currentUser.role, t),
                priority: 0,
                scope_type: 'current',
                is_system: false,
                is_active: true,
                created_at: '',
                updated_at: '',
                permissions: [],
            },
        ]
    }, [currentUser?.role, roles, t])

    const getUserFormValues = useCallback(
        (user: AdminUser) => ({
            display_name: user.display_name || user.username,
            account_value: user.username,
            role: user.role,
            is_active: user.is_active,
            region_id: user.region_id,
        }),
        []
    )

    const setUserFormValues = useCallback(
        (user: AdminUser) => {
            form.setFieldsValue(getUserFormValues(user))
        },
        [form, getUserFormValues]
    )

    const syncManagedAccountInput = useCallback(
        (fallbackValue?: string) => {
            const input = document.getElementById(
                managedUserAccountInputId
            ) as HTMLInputElement | null
            if (!input) {
                return
            }

            const fieldValue = form.getFieldValue('account_value')
            const likelyBrowserAutofillValue = currentLoginUser?.username
            const expectedValue = String(
                fieldValue === undefined ||
                    fieldValue === null ||
                    fieldValue === '' ||
                    fieldValue === likelyBrowserAutofillValue
                    ? fallbackValue ?? ''
                    : fieldValue
            )
            const shouldRestoreManagedAccount =
                input.value === '' ||
                input.value === likelyBrowserAutofillValue ||
                document.activeElement !== input

            if (
                expectedValue &&
                input.value !== expectedValue &&
                shouldRestoreManagedAccount
            ) {
                if (form.getFieldValue('account_value') !== expectedValue) {
                    form.setFieldValue('account_value', expectedValue)
                }
                input.value = expectedValue
                input.dispatchEvent(new Event('input', { bubbles: true }))
            }
        },
        [currentLoginUser?.username, form, managedUserAccountInputId]
    )

    const renderApiCredentialValue = useCallback(
        (value: string) => (
            <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {value}
            </span>
        ),
        []
    )

    useEffect(() => {
        void loadUsers()
        if (canReadRoles) {
            void loadRoles()
        } else {
            setRoles([])
            setRolesLoadError(false)
        }
    }, [canReadRoles, loadRoles, loadUsers])

    useEffect(() => {
        if (!userModalVisible || !currentUser) {
            return
        }

        const syncCurrentEditUser = () => {
            syncManagedAccountInput(currentUser.username)
        }

        syncCurrentEditUser()
        const timeoutIds = [0, 120, 600].map((delay) =>
            window.setTimeout(syncCurrentEditUser, delay)
        )

        return () => {
            timeoutIds.forEach((timeoutId) =>
                window.clearTimeout(timeoutId)
            )
        }
    }, [
        currentUser,
        syncManagedAccountInput,
        userFormInstanceKey,
        userModalVisible,
    ])

    const closeUserModal = useCallback(() => {
        userFormOpenSeqRef.current += 1
        setUserModalVisible(false)
        setCurrentUser(null)
        form.resetFields()
    }, [form])

    const handleCreate = () => {
        const nextOpenSeq = userFormOpenSeqRef.current + 1
        userFormOpenSeqRef.current = nextOpenSeq
        setUserFormInstanceKey(nextOpenSeq)
        setCurrentUser(null)
        form.resetFields()
        form.setFieldsValue({
            is_active: true,
            region_id: null,
        })
        setUserModalVisible(true)
        if (canCreateUsers) {
            void loadRoles({
                targetAccountType:
                    currentLoginUser?.role === PROJECT_MANAGER_ROLE_NAME
                        ? 'project'
                        : 'platform',
            })
        }
    }

    const getRoleLoadTargetAccountType = useCallback(
        (targetUser?: AdminUser | null): GetActiveRolesParams['targetAccountType'] =>
            currentLoginUser?.role === PROJECT_MANAGER_ROLE_NAME &&
            targetUser &&
            isProjectUser(targetUser)
                ? 'project'
                : 'platform',
        [currentLoginUser?.role]
    )

    const handleEdit = async (user: AdminUser) => {
        const nextOpenSeq = userFormOpenSeqRef.current + 1
        userFormOpenSeqRef.current = nextOpenSeq
        setUserFormInstanceKey(nextOpenSeq)
        setCurrentUser(user)
        form.resetFields()
        setUserFormValues(user)
        setUserModalVisible(true)
        if (canReadRoles || canAssignRoles || canUpdateUsers) {
            await loadRoles({
                targetAccountType: getRoleLoadTargetAccountType(user),
            })
        }
        if (userFormOpenSeqRef.current !== nextOpenSeq) {
            return
        }
        syncManagedAccountInput(user.username)
    }

    const handleOpenApiCredentialModal = (user: AdminUser) => {
        setApiCredentialUser(user)
        setGeneratedApiCredentials(null)
        setApiCredentialModalVisible(true)
    }

    const handleOpenPasswordModal = (user: AdminUser) => {
        setPasswordUser(user)
        setPasswordModalVisible(true)
        passwordForm.resetFields()
    }

    const handleClosePasswordModal = () => {
        setPasswordModalVisible(false)
        setPasswordUser(null)
        passwordForm.resetFields()
    }

    const isCurrentLoginUser = useCallback(
        (targetUser: AdminUser | null) => {
            if (!targetUser || !currentLoginUser?.id) {
                return false
            }
            return String(targetUser.id) === String(currentLoginUser.id)
        },
        [currentLoginUser?.id]
    )

    const isEditingProjectManagerSelf = Boolean(
        currentUser &&
            currentLoginUser?.role === PROJECT_MANAGER_ROLE_NAME &&
            isCurrentLoginUser(currentUser)
    )

    const isOtherSuperuser = useCallback(
        (targetUser: AdminUser) =>
            targetUser.is_superuser && !isCurrentLoginUser(targetUser),
        [isCurrentLoginUser]
    )

    const isSelfManagementRestricted = useCallback(
        (targetUser: AdminUser | null) =>
            Boolean(
                targetUser &&
                    !targetUser.is_superuser &&
                    isCurrentLoginUser(targetUser)
            ),
        [isCurrentLoginUser]
    )

    const isAdminReadonlyProjectUser = useCallback(
        (targetUser: AdminUser) =>
            currentLoginUser?.role === 'admin' && isProjectUser(targetUser),
        [currentLoginUser?.role]
    )

    const canChangeUserPassword = useCallback(
        (targetUser: AdminUser) =>
            canUpdateUserPasswords &&
            !isOtherSuperuser(targetUser) &&
            !isAdminReadonlyProjectUser(targetUser),
        [canUpdateUserPasswords, isAdminReadonlyProjectUser, isOtherSuperuser]
    )

    const handleSubmitPassword = async () => {
        if (!passwordUser) {
            return
        }

        try {
            const values = await passwordForm.validateFields()
            setPasswordLoading(true)
            if (isCurrentLoginUser(passwordUser)) {
                await updateCurrentUserPassword({
                    current_password: values.current_password,
                    new_password: values.new_password,
                })
            } else {
                await updateManagedUserPassword(passwordUser.id, {
                    new_password: values.new_password,
                })
            }
            message.success(t('user.messages.passwordUpdateSuccess'))
            handleClosePasswordModal()
        } catch (error: unknown) {
            if (isFormValidationError(error)) {
                return
            }
            message.error(
                formatApiError(error, t('user.messages.passwordUpdateFailed'))
            )
        } finally {
            setPasswordLoading(false)
        }
    }

    const handleSearchFilters = () => {
        const nextUsername = normalizeSearchText(usernameKeyword)
        setAppliedUsernameKeyword(nextUsername)
        setCurrentPage(1)
        void loadUsers(1, pageSize, nextUsername)
    }

    const handleResetFilters = () => {
        setUsernameKeyword('')
        setAppliedUsernameKeyword('')
        setCurrentPage(1)
        void loadUsers(1, pageSize, '')
    }

    const handleCloseApiCredentialModal = () => {
        setApiCredentialModalVisible(false)
        setApiCredentialUser(null)
        setGeneratedApiCredentials(null)

        if (shouldReloadUsersAfterCredentialChange) {
            setShouldReloadUsersAfterCredentialChange(false)
            void loadUsers(currentPage, pageSize, appliedUsernameKeyword)
        }
    }

    const handleSubmitUser = async () => {
        if (userSubmittingRef.current) {
            return
        }

        userSubmittingRef.current = true
        setUserSubmitting(true)
        try {
            const values = await form.validateFields()
            const displayName = values.display_name.trim()
            const username = values.account_value.trim()
            if (currentUser) {
                const updatePayload: UpdateUserRequest = {}

                if (canUpdateUsers && username !== currentUser.username) {
                    updatePayload.username = username
                }
                if (
                    canUpdateUsers &&
                    displayName !== currentUser.display_name
                ) {
                    updatePayload.display_name = displayName
                }
                if (
                    canUpdateUsers &&
                    values.region_id !== currentUser.region_id
                ) {
                    updatePayload.region_id = values.region_id ?? null
                }
                if (
                    canUpdateUsers &&
                    !isSelfManagementRestricted(currentUser) &&
                    values.is_active !== currentUser.is_active
                ) {
                    updatePayload.is_active = values.is_active
                }
                if (
                    !isEditingProjectManagerSelf &&
                    !isSelfManagementRestricted(currentUser) &&
                    (canAssignRoles || canUpdateUsers) &&
                    values.role !== currentUser.role
                ) {
                    updatePayload.role = values.role
                }

                if (Object.keys(updatePayload).length === 0) {
                    closeUserModal()
                    return
                }

                await updateUser(currentUser.id, updatePayload)
                if (
                    isCurrentLoginUser(currentUser) &&
                    updatePayload.is_active === false
                ) {
                    closeUserModal()
                    message.success(t('user.messages.selfDisabledLogout'))
                    logout()
                    return
                }
                message.success(t('user.messages.updateSuccess'))
            } else {
                await createUser({
                    username,
                    display_name: displayName,
                    password: values.password,
                    role: values.role,
                    is_active: values.is_active,
                    region_id: values.region_id ?? null,
                    ...(currentLoginUser?.role === PROJECT_MANAGER_ROLE_NAME
                        ? { account_type: 'project' as const }
                        : {}),
                })
                message.success(t('user.messages.createSuccess'))
            }
            closeUserModal()
            void loadUsers(currentPage, pageSize, appliedUsernameKeyword)
        } catch (error: unknown) {
            if (isFormValidationError(error)) {
                return
            }
            message.error(
                formatApiError(error, t('user.messages.operationFailed'))
            )
        } finally {
            userSubmittingRef.current = false
            setUserSubmitting(false)
        }
    }

    const handleRoleDropdownVisibleChange = (open: boolean) => {
        if (
            open &&
            (canReadRoles || canAssignRoles || canUpdateUsers) &&
            rolesLoadError
        ) {
            void loadRoles()
        }
    }

    const handleDelete = async (user: AdminUser) => {
        try {
            await deleteUser(user.id)
            message.success(t('user.messages.deleteSuccess'))

            const nextTotal = Math.max(total - 1, 0)
            const maxPage = Math.max(1, Math.ceil(nextTotal / pageSize))
            const nextPage = Math.min(currentPage, maxPage)
            if (nextPage !== currentPage) {
                setCurrentPage(nextPage)
            }
            void loadUsers(nextPage, pageSize, appliedUsernameKeyword)
        } catch (error: unknown) {
            message.error(
                formatApiError(error, t('user.messages.deleteFailed'))
            )
        }
    }

    const handleCopyApiCredentials = async (text: string) => {
        try {
            await copyTextToClipboard(text)
            message.success(t('user.apiCredentials.copySuccess'))
        } catch (error: unknown) {
            console.error('Failed to copy API credentials:', error)
            message.error(t('user.apiCredentials.copyFailed'))
        }
    }

    const handleCopyGeneratedApiCredentials = async () => {
        if (!generatedApiCredentials) {
            return
        }
        await handleCopyApiCredentials(
            `${t('user.apiCredentials.apiKey')}: ${generatedApiCredentials.api_key}\n` +
                `${t('user.apiCredentials.apiSecret')}: ${generatedApiCredentials.api_secret}`
        )
    }

    const handleResetApiCredentials = async (user: AdminUser) => {
        setApiCredentialLoading(true)
        try {
            const credentials = await resetUserApiCredentials(user.id)
            const nextUser: AdminUser = {
                ...user,
                api_key: credentials.api_key,
                has_api_credentials: true,
                api_secret_last4: credentials.api_secret_last4,
                api_credentials_updated_at: credentials.updated_at,
            }

            setGeneratedApiCredentials(credentials)
            syncUserInState(user.id, () => nextUser)
            setShouldReloadUsersAfterCredentialChange(true)
            message.success(t('user.messages.apiCredentialsResetSuccess'))
        } catch (error: unknown) {
            message.error(
                formatApiError(error, t('user.messages.operationFailed'))
            )
        } finally {
            setApiCredentialLoading(false)
        }
    }

    const handleRevokeApiCredentials = async (user: AdminUser) => {
        setApiCredentialLoading(true)
        try {
            await revokeUserApiCredentials(user.id)
            syncUserInState(user.id, (current) => ({
                ...current,
                api_key: null,
                has_api_credentials: false,
                api_secret_last4: null,
                api_credentials_updated_at: null,
            }))
            setGeneratedApiCredentials(null)
            setShouldReloadUsersAfterCredentialChange(true)
            message.success(t('user.messages.apiCredentialsRevoked'))
        } catch (error: unknown) {
            message.error(
                formatApiError(error, t('user.messages.operationFailed'))
            )
        } finally {
            setApiCredentialLoading(false)
        }
    }

    const handleConfirmRevokeApiCredentials = () => {
        if (!apiCredentialUser) {
            return
        }

        modal.confirm({
            title: t('user.confirmRevokeApiCredentials'),
            okText: t('common:buttons.ok'),
            cancelText: t('common:buttons.cancel'),
            okButtonProps: { danger: true, loading: apiCredentialLoading },
            onOk: () => handleRevokeApiCredentials(apiCredentialUser),
        })
    }

    const columns = [
        {
            title: t('user.form.displayName'),
            dataIndex: 'display_name',
            key: 'display_name',
            width: 190,
            ellipsis: true,
            render: (displayName: string | null, record: AdminUser) => {
                const projectUser = isProjectUser(record)
                return (
                    <Space size={4} style={{ maxWidth: '100%' }}>
                        {renderPaginatedListText(displayName || record.username)}
                        {projectUser && (
                            <Tag color="purple">
                                {t('user.labels.projectChildUser')}
                            </Tag>
                        )}
                    </Space>
                )
            },
        },
        {
            title: t('user.columns.username'),
            dataIndex: 'username',
            key: 'username',
            width: 150,
            ellipsis: true,
            render: renderPaginatedListText,
        },
        {
            title: t('user.columns.role'),
            dataIndex: 'role',
            key: 'role',
            width: 100,
            render: (role: string) => (
                <Tag color="blue">
                    {role ? getRoleDisplayName(role, t) : '-'}
                </Tag>
            ),
        },
        {
            title: t('user.columns.apiCredentialsStatus'),
            dataIndex: 'has_api_credentials',
            key: 'has_api_credentials',
            width: 140,
            render: (hasApiCredentials?: boolean) =>
                getApiCredentialStatusTag(hasApiCredentials),
        },
        {
            title: t('user.columns.isSuperuser'),
            dataIndex: 'is_superuser',
            key: 'is_superuser',
            width: 120,
            render: (isSuperuser: boolean) =>
                isSuperuser ? (
                    <Tag color="red">{t('user.labels.yes')}</Tag>
                ) : (
                    <Tag>{t('user.labels.no')}</Tag>
                ),
        },
        {
            title: t('user.columns.status'),
            dataIndex: 'is_active',
            key: 'is_active',
            width: 100,
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive
                        ? t('user.status.active')
                        : t('user.status.inactive')}
                </Tag>
            ),
        },
        {
            title: t('user.columns.createdAt'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (text: string) => localeService.formatDate(text),
        },
        {
            title: t('user.columns.actions'),
            key: 'action',
            width: 360,
            fixed: 'right' as const,
            render: (_: unknown, record: AdminUser) => {
                const readonlyProjectUser = isAdminReadonlyProjectUser(record)
                const selfRestricted = isSelfManagementRestricted(record)
                return (
                    <div style={tableActionStyle}>
                        {(canUpdateUsers || canAssignRoles) &&
                            !isOtherSuperuser(record) &&
                            !readonlyProjectUser && (
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEdit(record)}
                                >
                                    {t('user.actions.edit')}
                                </Button>
                            )}
                        {canChangeUserPassword(record) && (
                            <Button
                                type="link"
                                size="small"
                                icon={<KeyOutlined />}
                                onClick={() => handleOpenPasswordModal(record)}
                            >
                                {t('user.actions.changePassword')}
                            </Button>
                        )}
                        {canManageUserApiCredentials &&
                            !isOtherSuperuser(record) &&
                            !readonlyProjectUser && (
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<KeyOutlined />}
                                    onClick={() =>
                                        handleOpenApiCredentialModal(record)
                                    }
                                >
                                    {t('user.actions.manageApiCredentials')}
                                </Button>
                            )}
                        {canDeleteUsers &&
                            !record.is_superuser &&
                            !readonlyProjectUser &&
                            !selfRestricted && (
                                <Popconfirm
                                    title={t('user.confirmDelete')}
                                    open={openDeleteUserId === record.id}
                                    onOpenChange={(open) => {
                                        if (open) {
                                            setOpenDeleteUserId(record.id)
                                        }
                                    }}
                                    onCancel={() => setOpenDeleteUserId(null)}
                                    onConfirm={() => {
                                        setOpenDeleteUserId(null)
                                        handleDelete(record)
                                    }}
                                    okText={t('common:buttons.ok')}
                                    cancelText={t('common:buttons.cancel')}
                                >
                                    <Button
                                        type="link"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                    >
                                        {t('user.actions.delete')}
                                    </Button>
                                </Popconfirm>
                            )}
                    </div>
                )
            },
        },
    ]

    const apiCredentialModalFooter = generatedApiCredentials
        ? [
              <Button key="close" onClick={handleCloseApiCredentialModal}>
                  {t('common:buttons.cancel')}
              </Button>,
              <Button
                  key="copy"
                  icon={<CopyOutlined />}
                  onClick={() => {
                      void handleCopyGeneratedApiCredentials()
                  }}
              >
                  {t('user.apiCredentials.copyButton')}
              </Button>,
          ]
        : [
              <Button key="close" onClick={handleCloseApiCredentialModal}>
                  {t('common:buttons.cancel')}
              </Button>,
              ...(apiCredentialUser?.has_api_credentials
                  ? [
                        <Button
                            key="revoke"
                            danger
                            icon={<StopOutlined />}
                            loading={apiCredentialLoading}
                            onClick={handleConfirmRevokeApiCredentials}
                        >
                            {t('user.actions.revokeApiCredentials')}
                        </Button>,
                    ]
                  : []),
              <Button
                  key="reset"
                  type="primary"
                  icon={<KeyOutlined />}
                  loading={apiCredentialLoading}
                  onClick={() => {
                      if (apiCredentialUser) {
                          void handleResetApiCredentials(apiCredentialUser)
                      }
                  }}
              >
                  {apiCredentialUser?.has_api_credentials
                      ? t('user.actions.resetApiCredentials')
                      : t('user.actions.createApiCredentials')}
              </Button>,
          ]

    return (
        <PageLayout
            title={t('user.title')}
            breadcrumb={[
                { title: t('navigation:breadcrumb.systemManagement') },
                { title: t('navigation:breadcrumb.userManagement') },
            ]}
        >
            <FilterBar>
                <FilterField label={t('user.filters.usernameLabel')}>
                    <Input
                        allowClear
                        value={usernameKeyword}
                        placeholder={t('user.filters.usernamePlaceholder')}
                        onChange={(event) =>
                            setUsernameKeyword(event.target.value)
                        }
                        onPressEnter={handleSearchFilters}
                    />
                </FilterField>
                <FilterActions>
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={handleSearchFilters}
                        loading={loading}
                    >
                        {t('user.filters.search')}
                    </Button>
                    <Button onClick={handleResetFilters}>
                        {t('user.filters.reset')}
                    </Button>
                </FilterActions>
            </FilterBar>

            <PaginatedListContainer>
                {canCreateUsers && (
                    <PaginatedListToolbar>
                        <Button
                            type="primary"
                            icon={<UserAddOutlined />}
                            onClick={handleCreate}
                        >
                            {t('common:buttons.add', { defaultValue: '添加' })}
                        </Button>
                    </PaginatedListToolbar>
                )}

                <PaginatedListCard>
                    <Table
                        columns={columns}
                        dataSource={users}
                        rowKey="id"
                        loading={loading}
                        size={PAGINATED_LIST_TABLE_SIZE}
                        scroll={{ x: 1360 }}
                        pagination={buildPaginatedListPagination({
                            current: currentPage,
                            pageSize,
                            total,
                            showTotal: (count) =>
                                t('user.totalUsers', { total: count }),
                            onChange: (page, size) => {
                                setCurrentPage(page)
                                setPageSize(size)
                                void loadUsers(page, size, appliedUsernameKeyword)
                            },
                        })}
                    />
                </PaginatedListCard>
            </PaginatedListContainer>

            <Modal
                title={
                    currentUser
                        ? t('user.modal.editTitle')
                        : t('user.modal.createTitle')
                }
                open={userModalVisible}
                onOk={handleSubmitUser}
                onCancel={closeUserModal}
                okText={t('common:buttons.save')}
                cancelText={t('common:buttons.cancel')}
                confirmLoading={userSubmitting}
                width={USER_MODAL_WIDTH}
                maskClosable={false}
                destroyOnClose
                forceRender
                styles={{
                    body: modalBodyStyle,
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    autoComplete="off"
                    onValuesChange={(changedValues) => {
                        if (
                            Object.prototype.hasOwnProperty.call(
                                changedValues,
                                'role'
                            ) &&
                            (currentUser
                                ? canReadRoles ||
                                  canAssignRoles ||
                                  canUpdateUsers
                                : canCreateUsers)
                        ) {
                            void loadRoles()
                        }
                    }}
                >
                    <div aria-hidden="true" style={autofillDecoyStyle}>
                        <input
                            tabIndex={-1}
                            type="text"
                            name="username"
                            autoComplete="username"
                        />
                        <input
                            tabIndex={-1}
                            type="password"
                            name="password"
                            autoComplete="current-password"
                        />
                    </div>
                    <Form.Item
                        label={t('user.form.displayName')}
                        name="display_name"
                        rules={[
                            {
                                required: true,
                                whitespace: true,
                                message: t('user.form.displayNameRequired'),
                            },
                            {
                                min: 2,
                                max: 20,
                                message: t('user.form.displayNameLength'),
                            },
                            {
                                pattern: DISPLAY_NAME_PATTERN,
                                message: t('user.form.displayNamePattern'),
                            },
                        ]}
                    >
                        <Input disabled={!!currentUser && !canUpdateUsers} />
                    </Form.Item>
                    <Form.Item
                        label={t('user.form.username')}
                        name="account_value"
                        htmlFor={managedUserAccountInputId}
                        validateFirst
                        rules={[
                            {
                                required: true,
                                whitespace: true,
                                message: t('user.form.usernameRequired'),
                            },
                            {
                                min: USERNAME_MIN_LENGTH,
                                max: USERNAME_MAX_LENGTH,
                                message: t('user.form.usernameLength'),
                            },
                            {
                                pattern: USERNAME_PATTERN,
                                message: t('user.form.usernameFormat'),
                            },
                        ]}
                    >
                        <Input
                            id={managedUserAccountInputId}
                            name={managedUserAccountInputName}
                            autoComplete="new-password"
                            data-form-type="other"
                            onFocus={() =>
                                syncManagedAccountInput(currentUser?.username)
                            }
                            disabled={!!currentUser && !canUpdateUsers}
                        />
                    </Form.Item>
                    {!currentUser && (
                        <Form.Item
                            label={t('user.form.password')}
                            name="password"
                            rules={[
                                {
                                    required: true,
                                    message: t('user.form.passwordRequired'),
                                },
                                {
                                    validator(_, value) {
                                        if (
                                            !value ||
                                            isPasswordPolicyValid(value)
                                        ) {
                                            return Promise.resolve()
                                        }
                                        return Promise.reject(
                                            new Error(
                                                t('user.form.passwordPolicy')
                                            )
                                        )
                                    },
                                },
                            ]}
                        >
                            <Input.Password autoComplete="new-password" />
                        </Form.Item>
                    )}
                    <Form.Item
                        label={t('user.form.role')}
                        name="role"
                        rules={[
                            {
                                required: true,
                                message: t('user.form.rolesRequired'),
                            },
                        ]}
                        extra={
                            rolesLoadError ? (
                                <Typography.Text type="danger">
                                    {t('user.messages.roleLoadFailed')}
                                </Typography.Text>
                            ) : undefined
                        }
                    >
                        <Select
                            disabled={
                                (currentUser
                                    ? !canEditUserRole
                                    : !canCreateUsers) ||
                                isEditingProjectManagerSelf ||
                                isSelfManagementRestricted(currentUser)
                            }
                            loading={rolesLoading}
                            notFoundContent={
                                rolesLoadError ? (
                                    <Typography.Text type="danger">
                                        {t('user.messages.roleLoadFailed')}
                                    </Typography.Text>
                                ) : (
                                    t('user.form.noAssignableRoles')
                                )
                            }
                            onDropdownVisibleChange={
                                handleRoleDropdownVisibleChange
                            }
                        >
                            {roleSelectOptions.length > 0 ? (
                                roleSelectOptions.map((role) => (
                                    <Select.Option
                                        key={role.id}
                                        value={role.name}
                                    >
                                        {formatRoleOptionLabel(
                                            role.name,
                                            role.description,
                                            t
                                        )}
                                    </Select.Option>
                                ))
                            ) : currentUser?.role ? (
                                <Select.Option value={currentUser.role}>
                                    {getRoleDisplayName(currentUser.role, t)}
                                </Select.Option>
                            ) : null}
                        </Select>
                    </Form.Item>
                    <Form.Item name="region_id" hidden>
                        <RegionSelect allowClear={true} />
                    </Form.Item>
                    <Form.Item
                        label={t('user.form.status')}
                        name="is_active"
                        valuePropName="checked"
                    >
                        <Switch
                            disabled={
                                !!currentUser &&
                                (!canUpdateUsers ||
                                    isSelfManagementRestricted(currentUser))
                            }
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={t('user.passwordModal.title', {
                    username: passwordUser?.username ?? '',
                })}
                open={passwordModalVisible}
                onOk={() => {
                    void handleSubmitPassword()
                }}
                onCancel={handleClosePasswordModal}
                okText={t('common:buttons.save')}
                cancelText={t('common:buttons.cancel')}
                confirmLoading={passwordLoading}
                maskClosable={false}
                destroyOnClose
                forceRender
                styles={{
                    body: modalBodyStyle,
                }}
            >
                <Form form={passwordForm} layout="vertical">
                    {isCurrentLoginUser(passwordUser) && (
                        <Form.Item
                            label={t('user.passwordModal.currentPassword')}
                            name="current_password"
                            rules={[
                                {
                                    required: true,
                                    message: t(
                                        'user.passwordModal.currentPasswordRequired'
                                    ),
                                },
                            ]}
                        >
                            <Input.Password />
                        </Form.Item>
                    )}
                    <Form.Item
                        label={t('user.passwordModal.newPassword')}
                        name="new_password"
                        dependencies={['current_password']}
                        rules={[
                            {
                                required: true,
                                message: t(
                                    'user.passwordModal.newPasswordRequired'
                                ),
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (
                                        !value ||
                                        !isCurrentLoginUser(passwordUser) ||
                                        value !== getFieldValue('current_password')
                                    ) {
                                        return Promise.resolve()
                                    }
                                    return Promise.reject(
                                        new Error(
                                            t(
                                                'user.passwordModal.passwordReuse'
                                            )
                                        )
                                    )
                                },
                            }),
                            {
                                validator(_, value) {
                                    if (
                                        !value ||
                                        isPasswordPolicyValid(value)
                                    ) {
                                        return Promise.resolve()
                                    }
                                    return Promise.reject(
                                        new Error(
                                            t(
                                                'user.passwordModal.passwordPolicy'
                                            )
                                        )
                                    )
                                },
                            },
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        label={t('user.passwordModal.confirmPassword')}
                        name="confirm_password"
                        dependencies={['new_password']}
                        rules={[
                            {
                                required: true,
                                message: t(
                                    'user.passwordModal.confirmPasswordRequired'
                                ),
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    const newPassword =
                                        getFieldValue('new_password')
                                    if (
                                        !value ||
                                        !newPassword ||
                                        newPassword === value
                                    ) {
                                        return Promise.resolve()
                                    }
                                    return Promise.reject(
                                        new Error(
                                            t(
                                                'user.passwordModal.passwordMismatch'
                                            )
                                        )
                                    )
                                },
                            }),
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={t('user.apiCredentials.manageModalTitle', {
                    username: apiCredentialUser?.username ?? '',
                })}
                open={apiCredentialModalVisible}
                onCancel={handleCloseApiCredentialModal}
                footer={apiCredentialModalFooter}
                maskClosable={false}
                destroyOnClose
                styles={{
                    body: modalBodyStyle,
                }}
            >
                {generatedApiCredentials ? (
                    <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: '100%' }}
                    >
                        <Alert
                            type="warning"
                            showIcon
                            message={t('user.apiCredentials.copyNotice')}
                        />
                        <Descriptions
                            bordered
                            column={1}
                            title={t('user.apiCredentials.generatedInfoTitle')}
                            size="small"
                        >
                            <Descriptions.Item
                                label={t('user.apiCredentials.status')}
                            >
                                {getApiCredentialStatusTag(true)}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t('user.apiCredentials.apiKey')}
                            >
                                {renderApiCredentialValue(
                                    generatedApiCredentials.api_key
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t('user.apiCredentials.apiSecret')}
                            >
                                {renderApiCredentialValue(
                                    generatedApiCredentials.api_secret
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t('user.apiCredentials.updatedAt')}
                            >
                                {localeService.formatDate(
                                    generatedApiCredentials.updated_at
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    </Space>
                ) : (
                    <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: '100%' }}
                    >
                        <Descriptions
                            bordered
                            column={1}
                            title={t('user.apiCredentials.currentInfoTitle')}
                            size="small"
                        >
                            <Descriptions.Item
                                label={t('user.apiCredentials.status')}
                            >
                                {getApiCredentialStatusTag(
                                    apiCredentialUser?.has_api_credentials
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t('user.apiCredentials.apiKey')}
                            >
                                {apiCredentialUser?.api_key
                                    ? renderApiCredentialValue(
                                          apiCredentialUser.api_key
                                      )
                                    : t('user.apiCredentials.notConfigured')}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t('user.apiCredentials.secretLast4')}
                            >
                                {apiCredentialUser?.has_api_credentials
                                    ? apiCredentialUser.api_secret_last4 ||
                                      '----'
                                    : t('user.apiCredentials.notConfigured')}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t('user.apiCredentials.updatedAt')}
                            >
                                {apiCredentialUser?.api_credentials_updated_at
                                    ? localeService.formatDate(
                                          apiCredentialUser.api_credentials_updated_at
                                      )
                                    : '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        {!apiCredentialUser?.has_api_credentials && (
                            <Alert
                                type="info"
                                showIcon
                                message={t('user.apiCredentials.emptyState')}
                            />
                        )}
                    </Space>
                )}
            </Modal>
        </PageLayout>
    )
}

export default UserManagement
